export const vertexShaderSource = `#version 300 es
precision highp float;

layout(location = 0) in vec3 a_position;
layout(location = 1) in vec3 a_normal;
layout(location = 2) in vec2 a_uv;
layout(location = 3) in float a_texIndex;

uniform mat4 u_model;
uniform mat4 u_view;
uniform mat4 u_projection;

out vec3 v_normal;
out vec3 v_worldPosition;
out vec2 v_uv;
flat out int v_texIndex;

void main() {
    vec4 worldPosition = u_model * vec4(a_position, 1.0);
    v_worldPosition = worldPosition.xyz;
    v_normal = mat3(u_model) * a_normal;
    v_uv = a_uv;
    v_texIndex = int(a_texIndex);
    gl_Position = u_projection * u_view * worldPosition;
}
`;

export const DEFAULT_MAX_LIGHTS = 30;

export function getFragmentShaderSource(maxLights: number): string {
  return `#version 300 es
precision highp float;

#define MAX_STAR_LIGHTS ${maxLights}

in vec3 v_normal;
in vec3 v_worldPosition;
in vec2 v_uv;
flat in int v_texIndex;

uniform vec3 u_color;
uniform vec3 u_ambientColor;
uniform vec3 u_playerLightPos;
uniform vec3 u_playerLightColor;
uniform float u_playerLightRadius;

// Star lights
uniform vec3 u_starLightPositions[MAX_STAR_LIGHTS];
uniform vec3 u_starLightColors[MAX_STAR_LIGHTS];
uniform int u_starLightCount;
uniform float u_starLightRadius;

// Debug point light (simple, no shadows)
uniform vec3 u_debugLightPos;
uniform vec3 u_debugLightColor;
uniform float u_debugLightRadius;
uniform bool u_debugLightEnabled;

// Ceiling light start index in star lights array
uniform int u_ceilingLightStartIndex;

// Dungeon grid for shadow casting
uniform sampler2D u_dungeonGrid;
uniform vec2 u_dungeonSize;  // width, height in cells
uniform float u_cellSize;

// Surface textures
uniform sampler2D u_surfaceTexture;
uniform bool u_useTexture;

// Wall texture array (4 textures)
uniform sampler2D u_wallTextures[4];
uniform bool u_useWallTexture;

// Specular/reflective rendering
uniform bool u_useSpecular;
uniform vec3 u_viewPos;
uniform float u_shininess;

// Cubemap reflection
uniform bool u_useCubemap;
uniform samplerCube u_cubemap;
uniform float u_reflectivity;

out vec4 fragColor;

// Check if a cell is solid (returns true if blocked)
bool isCellSolid(int cellX, int cellZ) {
    if (cellX < 0 || cellX >= int(u_dungeonSize.x) || cellZ < 0 || cellZ >= int(u_dungeonSize.y)) {
        return true;
    }
    vec2 uv = (vec2(float(cellX), float(cellZ)) + 0.5) / u_dungeonSize;
    float value = texture(u_dungeonGrid, uv).r;
    return value < 0.5;  // 0 = solid, 1 = open
}

// Ray march from point to light, return true if path is clear
bool isLightVisible(vec3 fromPos, vec3 toPos) {
    vec3 dir = toPos - fromPos;
    float dist = length(dir);
    dir = normalize(dir);

    // Step through the grid
    float stepSize = u_cellSize * 0.4;  // Step size smaller than cell
    int maxSteps = int(dist / stepSize) + 1;

    for (int i = 1; i < 50; i++) {  // Max 50 steps
        if (i >= maxSteps) break;

        float t = float(i) * stepSize;
        if (t >= dist - stepSize) break;  // Don't check at light position

        vec3 samplePos = fromPos + dir * t;
        int cellX = int(floor(samplePos.x / u_cellSize));
        int cellZ = int(floor(samplePos.z / u_cellSize));

        if (isCellSolid(cellX, cellZ)) {
            return false;
        }
    }

    return true;
}

// Soft ray march: samples grid with LINEAR filtering for smooth shadow edges
float lightVisibilitySoft(vec3 fromPos, vec3 toPos) {
    vec3 dir = toPos - fromPos;
    float dist = length(dir);
    dir = normalize(dir);

    float visibility = 1.0;
    float stepSize = u_cellSize * 0.4;
    int maxSteps = int(dist / stepSize) + 1;

    for (int i = 1; i < 50; i++) {
        if (i >= maxSteps) break;

        float t = float(i) * stepSize;
        if (t >= dist - stepSize) break;

        vec3 samplePos = fromPos + dir * t;
        vec2 uv = vec2(samplePos.x, samplePos.z) / (u_cellSize * u_dungeonSize);
        float openness = texture(u_dungeonGrid, uv).r;
        // Remap: values >= 0.5 (wall boundary) are fully open,
        // values < 0.5 (inside solid) create a soft shadow gradient.
        // Multiplicative so grazing rays (one boundary sample) aren't dimmed,
        // but rays through walls (multiple solid samples) are fully blocked.
        visibility *= smoothstep(0.0, 0.5, openness);
    }

    return visibility;
}

void main() {
    vec3 normal = normalize(v_normal);
    vec3 lighting = u_ambientColor;
    vec3 specularTotal = vec3(0.0);
    vec3 viewDir = normalize(u_viewPos - v_worldPosition);

    // Point light from player position (no shadow for player light)
    vec3 lightDir = u_playerLightPos - v_worldPosition;
    float distance = length(lightDir);
    lightDir = normalize(lightDir);

    float attenuation = max(0.0, 1.0 - distance / u_playerLightRadius);
    attenuation = attenuation * attenuation;

    float diffuse = max(dot(normal, lightDir), 0.0);
    lighting += u_playerLightColor * diffuse * attenuation;

    // Specular highlight from player light
    if (u_useSpecular && attenuation > 0.0) {
        vec3 reflectDir = reflect(-lightDir, normal);
        float spec = pow(max(dot(viewDir, reflectDir), 0.0), u_shininess);
        specularTotal += u_playerLightColor * spec * attenuation * 2.0;
    }

    // Debug point light (basic inverse square, with shadow casting)
    if (u_debugLightEnabled && isLightVisible(v_worldPosition, u_debugLightPos)) {
        vec3 debugLightDir = u_debugLightPos - v_worldPosition;
        float debugDistance = length(debugLightDir);
        debugLightDir = normalize(debugLightDir);

        // Inverse square falloff
        float debugNormalizedDist = debugDistance / u_debugLightRadius;
        float debugAttenuation = 1.0 / (1.0 + debugNormalizedDist * debugNormalizedDist);

        float debugDiffuse = max(dot(normal, debugLightDir), 0.0);
        lighting += u_debugLightColor * debugDiffuse * debugAttenuation;
    }

    // Star lights (directional spotlights pointing down)
    vec3 spotDirection = vec3(0.0, -1.0, 0.0);  // Pointing down

    for (int i = 0; i < MAX_STAR_LIGHTS; i++) {
        if (i >= u_starLightCount) break;

        vec3 starLightPos = u_starLightPositions[i];
        vec3 toSurface = v_worldPosition - starLightPos;
        float starDistance = length(toSurface);
        vec3 toSurfaceDir = normalize(toSurface);

        // Spotlight cone: sharp cone for stars, smooth power falloff for ceiling lights
        float spotAngle = dot(toSurfaceDir, spotDirection);
        float spotFactor;
        if (i < u_ceilingLightStartIndex) {
            spotFactor = smoothstep(0.4, 1.0, spotAngle);
        } else {
            // Smooth quadratic falloff with no hard edge
            spotFactor = pow(max(spotAngle, 0.0), 2.0);
        }

        // Inverse square falloff
        float starNormalizedDist = starDistance / u_starLightRadius;
        float starAttenuation = 1.0 / (1.0 + starNormalizedDist * starNormalizedDist);

        // Soft shadow check for ceiling lights
        float shadowFactor = 1.0;
        if (i >= u_ceilingLightStartIndex) {
            shadowFactor = lightVisibilitySoft(v_worldPosition, starLightPos);
        }

        // Diffuse lighting (light direction is from surface to light)
        vec3 starLightDir = -toSurfaceDir;
        float starDiffuse = max(dot(normal, starLightDir), 0.0);

        lighting += u_starLightColors[i] * starDiffuse * starAttenuation * spotFactor * shadowFactor;

        // Ceiling glow: radial falloff based on horizontal distance
        // Only affects downward-facing surfaces (ceiling)
        float horizDist = length(toSurface.xz);
        float ceilingGlow = max(0.0, 1.0 - horizDist / 1.5);
        ceilingGlow = ceilingGlow * ceilingGlow;
        float ceilingFactor = max(-normal.y, 0.0);
        lighting += u_starLightColors[i] * ceilingGlow * ceilingFactor * 0.3 * shadowFactor;
    }

    // Get base color from texture or uniform
    vec3 baseColor;
    if (u_useWallTexture) {
        // Decode texture index and UV transform from combined value
        // texInfo = textureId + transformId * 4
        int texInfo = v_texIndex;
        int textureId = texInfo % 4;
        int transformId = texInfo / 4;

        // Apply UV transformation
        vec2 uv = v_uv;
        // First apply flip if transformId >= 4
        if (transformId >= 4) {
            uv.x = 1.0 - uv.x;
            transformId -= 4;
        }
        // Then apply rotation (0=none, 1=90°, 2=180°, 3=270°)
        if (transformId == 1) {
            uv = vec2(uv.y, 1.0 - uv.x);
        } else if (transformId == 2) {
            uv = vec2(1.0 - uv.x, 1.0 - uv.y);
        } else if (transformId == 3) {
            uv = vec2(1.0 - uv.y, uv.x);
        }

        // Sample from the appropriate wall texture
        if (textureId == 0) {
            baseColor = texture(u_wallTextures[0], uv).rgb;
        } else if (textureId == 1) {
            baseColor = texture(u_wallTextures[1], uv).rgb;
        } else if (textureId == 2) {
            baseColor = texture(u_wallTextures[2], uv).rgb;
        } else {
            baseColor = texture(u_wallTextures[3], uv).rgb;
        }
    } else if (u_useTexture) {
        baseColor = texture(u_surfaceTexture, v_uv).rgb;
    } else {
        baseColor = u_color;
    }

    vec3 finalColor = baseColor * lighting;

    // Add specular highlights (additive, not multiplied by base color)
    if (u_useSpecular) {
        finalColor += specularTotal;
    }

    // Cubemap reflection (mirror-like)
    if (u_useCubemap) {
        vec3 I = normalize(v_worldPosition - u_viewPos);
        vec3 R = reflect(I, normal);
        vec3 reflectedColor = texture(u_cubemap, R).rgb;
        // Blend between base color and reflection
        finalColor = mix(finalColor, reflectedColor, u_reflectivity);
    }

    fragColor = vec4(finalColor, 1.0);
}
`;
}

export function createShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

export function createProgram(gl: WebGL2RenderingContext, vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram | null {
  const program = gl.createProgram();
  if (!program) return null;

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }

  return program;
}
